import { useMemo } from "react";
import { apiClient, useAuth } from "@/lib/auth";
import { useNexOs } from "@/lib/nexOs";
import { useOps } from "@/lib/opsCards";
import { RESIDENT_ACTION_CAPS, groupResidentActions, isResidentActionChild } from "@/lib/residentActions";

export const SYSTEM_CONSOLE_ID = "system_console";

// Built-in System Console links — rendered as link cards when moved into an engine.
const CONSOLE_LINKS = [
  ["/admin/roi", "line-chart", "Credit ROI Dashboard", "Super Admin · ECU burn + profit tracker", true],
  ["/admin/pillar-health", "heart-pulse", "Pillar Health", "Super Admin · pillar pipeline + AI budget", true],
  ["/admin/lesson-voice", "audio-lines", "Lesson Voice", "Super Admin · compare + pick the homework narrator · credits", true],
  ["/admin/archive-center", "archive", "Archive Center", "Partners, vendors, properties, templates, tasks · group archive · SA delete"],
  ["/admin/archived-funds", "piggy-bank", "Archived Funds", "Super Admin · money from archived residents → program fees / admin cost", true],
  ["/admin/db", "database", "Database Manager", "Archive + hard delete rows across every collection", true],
  ["/admin/billing", "receipt", "Credits & Billing", "Burn rate · topup ledger · daily usage"],
  ["/admin/briefs", "inbox", "Briefs Inbox", "Every friction audit brief · triage"],
  ["/admin/users", "users", "Users & Roles", "Invite staff · assign permissions"],
  ["/admin/previews", "link", "Client Micro-Landings", "Preview slots + access codes"],
  ["/admin/demos", "building-2", "Demo Directory", "Business demos & 5-digit access codes"],
  ["/admin/tutorials", "graduation-cap", "Tutorials Manager", "Onboarding walk-throughs"],
  ["/admin/nds", "layers", "Schema Editor", "Full NDS-1 JSON editor"],
  ["/admin/layout", "workflow", "Layout Rules", "Section visibility + workflow logic"],
  ["/admin/labels", "tag", "Labels Editor", "Every UI string in one place"],
  ["/admin/settings", "key-round", "PIN & Security", "Set your admin PIN"],
  ["/admin/contract-vendors", "hard-hat", "Contract Vendors", "Invite vendors · assign their workers · review hours & invoices"],
  ["/admin/live-users", "radio", "Live Users", "Super Admin · who is online now, where they are, how long", true],
  ["/admin/finance/bank", "landmark", "Bank", "Citizens ...5039 (primary) · TEA-First Reliance ...1403 · Petty Cash · deposits", false, "finance_engine"],
  ["/admin/finance/debt", "receipt", "Debt", "Nov 1 cutover · Exhibit A / B debt schedule · bills · monthly outflows", false, "finance_engine"],
  ["/admin/finance/distributions", "pie-chart", "Distributions", "Super Admin · per-partner share · record distributions paid · history", true, "finance_engine"],
];

export const CONSOLE_CAPS = CONSOLE_LINKS.map(([to, icon, label, desc, sa, vertical]) => ({
  id: `sys${to.replace(/\W+/g, "_")}`, label, description: desc, icon, href: to, sys: true, superAdminOnly: !!sa,
  modal_kind: "open_module", status: "LIVE", tone: "live", build_tier: 1, est_credits: 0, vertical_id: vertical || SYSTEM_CONSOLE_ID,
}));

export const isSuperAdminUser = (u) => u?.role === "super_admin" || !!u?.is_super_admin;

export const BANK_CARD = "sys_admin_finance_bank";
export const DEBT_CARD = "sys_admin_finance_debt";
export const SPECIAL_CARDS = [BANK_CARD, DEBT_CARD];
export const canEditCard = (u, id) => isSuperAdminUser(u) || u?.card_access?.[id] === "edit";

// Pop action for a card — honours attached quick forms from the owner card config.
export const capAction = (cap, cfg) => {
  const label = cfg?.label || cap.label;
  const description = cfg?.description || cap.description;
  const href = cfg?.link_href || cap.href;
  return cfg?.forms?.length
    ? { kind: "quickForms", title: label, description, href, forms: cfg.forms, docs: cfg.docs }
    : { kind: cap.modal_kind || "open_module", title: label, description, href };
};

// Every card + console item with its effective placement (deleted items dropped).
export function usePlacedItems() {
  const { schema } = useNexOs();
  const ops = useOps();
  const { user } = useAuth();
  const vis = isSuperAdminUser(user) ? null : user?.visible_cards;
  return useMemo(() => {
    const all = [...(schema?.capabilities_template || []), ...CONSOLE_CAPS];
    const notDeleted = (c) => !ops.cards[c.id]?.deleted_at;
    return groupResidentActions(all, (c) => notDeleted(c) && (!vis || vis.includes(c.id)), notDeleted)
      .map((c) => {
        const cfg = ops.cards[c.id];
        return cfg?.vertical_id ? { ...c, vertical_id: cfg.vertical_id, placed_at: cfg.placed_at } : c;
      })
      .sort((a, b) => (a.placed_at || "").localeCompare(b.placed_at || ""));
  }, [schema, ops.cards, vis]);
}

/** The six cards inside the Resident Actions folder that this user may open. */
export function useResidentActionCaps() {
  const { schema } = useNexOs();
  const ops = useOps();
  const { user } = useAuth();
  const vis = isSuperAdminUser(user) ? null : user?.visible_cards;
  return useMemo(() => (schema?.capabilities_template || [])
    .filter((c) => isResidentActionChild(c.id) && !ops.cards[c.id]?.deleted_at && (!vis || vis.includes(c.id)))
    .sort((a, b) => RESIDENT_ACTION_CAPS.indexOf(a.id) - RESIDENT_ACTION_CAPS.indexOf(b.id)), [schema, ops.cards, vis]);
}

export const capsForVertical = (items, vid) => (vid === "all_engines"
  ? items.filter((c) => c.vertical_id !== SYSTEM_CONSOLE_ID)
  : items.filter((c) => c.vertical_id === vid));

export const useIsSuperAdmin = () => isSuperAdminUser(useAuth().user);
/** Super admins have every permission; everyone else via their own permissions plus their role's. */
export const useHasPerm = (key) => { const u = useAuth().user; return isSuperAdminUser(u) || (u?.staff_permissions || []).includes(key); };

export const moveItem = (id, target) => apiClient.put(`/admin/ops/placement/${id}`, { target });
export const deleteConsoleItem = (id) => apiClient.delete(`/admin/ops/placement/${id}`);
